import { defineModule } from 'direct-vuex'
import { moduleActionContext, moduleGetterContext } from '@/store'
import * as graphSdk from '@/api/graphql-sdk.generated'
import * as graph from '@shared/types/graphql'
import api from '@/api'
import { Question, Option } from './survey-store-module'

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

export type TestConfig = NonNullable<graphSdk.TestConfigPublicQuery['testConfig']>
export type TestMetaDetails = NonNullable<graphSdk.TestConfigPublicQuery['testConfig']>['testMetaDetails']
export type QuestionConfigs = TestConfig['questionConfigs']
export type QuestionConfiguration = QuestionConfigs[0]
export type OptionConfigs = TestConfig['optionConfigs']
export type OptionConfiguration = PartialBy<OptionConfigs[0], 'id'>
export type Findings = TestConfig['findings']

export interface SurveyConfigurationState {
  workspaceId?: string,
  testId?: string,
  sharedLinkHash?: string
  questionConfigs: Record<string, QuestionConfiguration>
  optionConfigs: Record<string, OptionConfiguration>
  findings?: string[],
  testMetaDetails?: TestMetaDetails
}

const createSurveyConfigurationState = (): SurveyConfigurationState => ({
  workspaceId: undefined,
  testId: undefined,
  sharedLinkHash: undefined,
  questionConfigs: {},
  optionConfigs: {},
  findings: [],
  testMetaDetails: undefined
})

const surveyConfiguration = defineModule({
  namespaced: true,
  state: createSurveyConfigurationState,
  mutations: {
    reset (state) {
      Object.assign(state, createSurveyConfigurationState())
    },
    setQuestionConfigs (state, payload: Record<string, QuestionConfiguration>) {
      state.questionConfigs = {
        ...state.questionConfigs,
        ...payload
      }
    },
    setOptionConfigs (state, payload: Record<string, OptionConfiguration>) {
      state.optionConfigs = {
        ...state.optionConfigs,
        ...payload
      }
    },
    fillInQuestionConfigs (state, payload: Record<string, QuestionConfiguration>) {
      state.questionConfigs = {
        ...payload,
        ...state.questionConfigs
      }
    },
    fillInOptionConfigs (state, payload: Record<string, OptionConfiguration>) {
      state.optionConfigs = {
        ...payload,
        ...state.optionConfigs
      }
    },
    updateQuestionConfig (state, payload: Partial<QuestionConfiguration> & { questionId: string }) {
      const existingConfig = state.questionConfigs[payload.questionId]

      const newConfig = {
        ...existingConfig,
        ...payload
      }
      state.questionConfigs = {
        ...state.questionConfigs,
        [payload.questionId]: newConfig
      }
    },
    updateOptionConfig (state, payload: Partial<OptionConfiguration> & { questionOptionId: number }) {
      const existingConfig = state.optionConfigs[payload.questionOptionId]

      const newConfig = {
        ...existingConfig,
        ...payload
      }
      state.optionConfigs = {
        ...state.optionConfigs,
        [payload.questionOptionId]: newConfig
      }
    },
    setSharedLinkHash (state, payload?: string) {
      state.sharedLinkHash = payload
    },
    setFindings (state, payload: string[]) {
      state.findings = payload
    },
    setWorkspaceTestId (state, payload: { testId?: string, workspaceId?: string}) {
      state.testId = payload.testId
      state.workspaceId = payload.workspaceId
    },
    setTestMetaDetails (state, payload: TestMetaDetails) {
      state.testMetaDetails = payload
    }
  },
  getters: {
    questionConfigById: (state) => (questionId: string): QuestionConfiguration => {
      return state.questionConfigs[questionId]
    },
    optionConfigById: (state) => (optionId: number): OptionConfiguration => {
      return state.optionConfigs[optionId]
    }
  },
  actions: {
    reset (context) {
      const { commit } = SurveyConfigurationActionContext(context)
      commit.reset()
    },
    addDefaultsForUnconfiguredQuestions (context, questions: Question[]): void {
      const { commit } = SurveyConfigurationActionContext(context)
      const seedQuestionConfigs:Record<string, QuestionConfiguration> = {}
      questions.forEach((q) => {
        seedQuestionConfigs[q.id] = { questionId: q.id, visible: true, title: undefined, context: undefined }
      })
      commit.fillInQuestionConfigs(seedQuestionConfigs)
      const options:Option[] = questions?.reduce((acc: Option[], q) => acc.concat(q.options || []), []) || []

      const seedOptionConfigs:Record<string, OptionConfiguration> = {}
      options.forEach((o) => {
        seedOptionConfigs[o.id] = { questionOptionId: o.id, title: undefined }
      })

      commit.fillInOptionConfigs(seedOptionConfigs)
    },
    async fetchTestConfigurationsWithMeta (context, payload: graph.QueryTestConfigArgs): Promise<void> {
      const { commit, dispatch } = SurveyConfigurationActionContext(context)
      const testConfigResponse = await api.getPublicTestConfiguration(payload)
      if (!testConfigResponse) {
        console.error('Failed to fetch public configurations for survey', payload.workspaceId, payload.testId)
        return
      }
      await commit.setTestMetaDetails(testConfigResponse.testMetaDetails)
      await dispatch.setTestConfigs(testConfigResponse)
    },
    async fetchTestConfigurations (context, payload: graph.QueryTestConfigArgs) {
      const { dispatch } = SurveyConfigurationActionContext(context)
      const testConfigResponse = await api.getTestConfiguration(payload)
      if (!testConfigResponse) {
        console.error('Failed to fetch configurations for survey', payload.workspaceId, payload.testId)
        return
      }
      await dispatch.setTestConfigs(testConfigResponse)
    },
    async setTestConfigs (context, testConfigResponse: graphSdk.TestConfigQuery['testConfig']) {
      const { state, commit } = SurveyConfigurationActionContext(context)

      const questionConfigs = testConfigResponse?.questionConfigs.reduce((configs: typeof state['questionConfigs'], config) => {
        const { questionId } = config
        configs[questionId] = {
          ...config
        }
        return configs
      }, {})

      const optionConfigs = testConfigResponse?.optionConfigs.reduce((configs: typeof state['optionConfigs'], config) => {
        const { questionOptionId } = config
        configs[questionOptionId] = {
          ...config
        }
        return configs
      }, {})

      commit.setWorkspaceTestId({
        testId: testConfigResponse?.testId,
        workspaceId: testConfigResponse?.workspaceId
      })
      commit.setQuestionConfigs(questionConfigs || {})
      commit.setOptionConfigs(optionConfigs || {})
      commit.setFindings(testConfigResponse?.findings || state.findings || [])
      commit.setSharedLinkHash(testConfigResponse?.sharedLinkHash)
    },
    async saveFindings (context, findings: string[]) {
      const { commit, state, rootState } = SurveyConfigurationActionContext(context)
      const { survey } = rootState.survey
      const payload = {
        workspaceId: state.workspaceId!,
        testId: state.testId!,
        surveyId: survey!.id,
        findings
      }
      const resp = await api.upsertTestConfig(payload)
      commit.setFindings(resp.configuration?.findings || [])
    },
    async toggleSharedLink (context, payload: {workspaceId: string, testId: string, createLink: boolean}) {
      const { commit } = SurveyConfigurationActionContext(context)
      const resp = await api.toggleSharedLink(payload)
      if (resp.success) {
        commit.setSharedLinkHash(resp.shareLinkHash)
      } else {
        console.error('Failed to create a new shared link hash', payload.workspaceId, payload.testId)
      }
      return resp.success
    },
    async updateQuestionConfig (context, questionConfig: Partial<QuestionConfiguration> & { questionId: string }) {
      const { state, rootState, commit } = SurveyConfigurationActionContext(context)
      const { title, questionId, visible, context: questionContext } = {
        ...state.questionConfigs[questionConfig.questionId],
        ...questionConfig
      }
      const payload = {
        workspaceId: state.workspaceId!,
        testId: state.testId!,
        surveyId: rootState.survey.survey!.id,
        findings: state.findings,
        questionConfigs: [
          {
            title,
            questionId,
            visible,
            context: questionContext
          }
        ]
      }
      const resp = await api.upsertTestConfig(payload)
      const updatedQuestionConfig = resp.configuration!.questionConfigs.find(q => q.questionId === questionConfig.questionId)

      if (updatedQuestionConfig) {
        commit.updateQuestionConfig(updatedQuestionConfig)
      }
    },
    async deleteQuestionText (context, questionConfig: {questionId: string, type: 'title' | 'context' }) {
      const { state, commit, rootState } = SurveyConfigurationActionContext(context)

      const newQuestionConfig = Object.assign(state.questionConfigs[questionConfig.questionId])
      newQuestionConfig[questionConfig.type] = undefined
      newQuestionConfig.id = undefined
      newQuestionConfig.surveyId = undefined
      const payload = {
        workspaceId: state.workspaceId!,
        testId: state.testId!,
        surveyId: rootState.survey.survey!.id,
        findings: state.findings,
        questionConfigs: [newQuestionConfig]
      }
      const resp = await api.upsertTestConfig(payload)
      const updatedQuestionConfig = resp.configuration!.questionConfigs.find(q => q.questionId === questionConfig.questionId)
      if (updatedQuestionConfig) {
        commit.updateQuestionConfig(updatedQuestionConfig)
      }
    },
    async updateOptionConfig (context, optionConfig: Partial<OptionConfiguration> & { questionOptionId: number }) {
      const { state, rootState, commit } = SurveyConfigurationActionContext(context)
      const { title, questionOptionId } = {
        ...state.optionConfigs[optionConfig.questionOptionId],
        ...optionConfig
      }
      const payload = {
        workspaceId: state.workspaceId!,
        testId: state.testId!,
        surveyId: rootState.survey.survey!.id,
        findings: state.findings,
        optionConfigs: [
          {
            title,
            questionOptionId
          }
        ]
      }
      const resp = await api.upsertTestConfig(payload)
      const updatedOptionConfig = resp.configuration!.optionConfigs.find(o => o.questionOptionId === optionConfig.questionOptionId)
      if (updatedOptionConfig) {
        commit.updateOptionConfig(updatedOptionConfig)
      }
    },
    async deleteOptionText (context, questionOptionId: number) {
      const { state, commit, rootState } = SurveyConfigurationActionContext(context)

      const newOptionConfig = Object.assign(state.optionConfigs[questionOptionId])
      newOptionConfig.title = undefined
      newOptionConfig.id = undefined
      const payload = {
        workspaceId: state.workspaceId!,
        testId: state.testId!,
        surveyId: rootState.survey.survey!.id,
        findings: state.findings,
        optionConfigs: [newOptionConfig]
      }
      const resp = await api.upsertTestConfig(payload)
      const updatedOptionConfig = resp.configuration!.optionConfigs.find(o => o.questionOptionId === questionOptionId)
      if (updatedOptionConfig) {
        commit.updateOptionConfig(updatedOptionConfig)
      }
    }
  }
})

export default surveyConfiguration
export const SurveyConfigurationGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, surveyConfiguration)
export const SurveyConfigurationActionContext = (context:any) => moduleActionContext(context, surveyConfiguration)
